import {
  CheckCircle2,
  Loader2,
  XCircle,
  AlertTriangle,
} from "lucide-react";
import type { LucideIcon } from "lucide-react";
import type { ReactNode } from "react";
import { Crossfade } from "./Crossfade";

export type TokenPageStatus = "loading" | "success" | "error" | "expired" | "consumed" | "no-token";

interface TokenStatePageProps {
  status: TokenPageStatus;
  /** Headline per status — provided by the page. */
  loadingTitle?: string;
  loadingBody?: string;
  successTitle?: string;
  successBody?: ReactNode;
  successAction?: ReactNode;
  errorTitle?: string;
  errorBody?: ReactNode;
  errorAction?: ReactNode;
  expiredTitle?: string;
  expiredBody?: ReactNode;
  expiredAction?: ReactNode;
  consumedTitle?: string;
  consumedBody?: ReactNode;
  consumedAction?: ReactNode;
  noTokenTitle?: string;
  noTokenBody?: ReactNode;
  noTokenAction?: ReactNode;
}

const STATUS_ICON: Record<TokenPageStatus, LucideIcon> = {
  loading: Loader2,
  success: CheckCircle2,
  error: XCircle,
  expired: AlertTriangle,
  consumed: AlertTriangle,
  "no-token": XCircle,
};

const STATUS_ICON_COLOR: Record<TokenPageStatus, string> = {
  loading: "var(--muted-foreground)",
  success: "var(--confidence)",
  error: "var(--enforcement)",
  expired: "var(--review)",
  consumed: "var(--review)",
  "no-token": "var(--enforcement)",
};

/**
 * TokenStatePage — shared state-machine wrapper for the 4 token-only pages:
 * CompleteSignUp / ResetPassword / VerifyEmail / MagicLink / AcceptInvite.
 *
 * Each page calls this with a `status` plus per-status content. The wrapper
 * handles the visual: centered icon, h1 (programmatically focused on state
 * change), body, optional action row.
 *
 * `loading` rotates the spinner via motion-reduce-safe `animate-spin` and
 * exposes `role="status" aria-live="polite"` so SR users hear "Verifying…".
 */
export function TokenStatePage({
  status,
  loadingTitle = "Loading…",
  loadingBody,
  successTitle,
  successBody,
  successAction,
  errorTitle,
  errorBody,
  errorAction,
  expiredTitle,
  expiredBody,
  expiredAction,
  consumedTitle,
  consumedBody,
  consumedAction,
  noTokenTitle,
  noTokenBody,
  noTokenAction,
}: TokenStatePageProps) {
  const Icon = STATUS_ICON[status];
  const iconColor = STATUS_ICON_COLOR[status];

  const { title, body, action } = (() => {
    switch (status) {
      case "loading":
        return { title: loadingTitle, body: loadingBody, action: null };
      case "success":
        return { title: successTitle, body: successBody, action: successAction };
      case "error":
        return { title: errorTitle, body: errorBody, action: errorAction };
      case "expired":
        return { title: expiredTitle, body: expiredBody, action: expiredAction };
      case "consumed":
        return { title: consumedTitle, body: consumedBody, action: consumedAction };
      case "no-token":
        return { title: noTokenTitle, body: noTokenBody, action: noTokenAction };
    }
  })();

  const isLoading = status === "loading";

  return (
    <Crossfade stateKey={status} focusOnEnter="h1">
      <div className="text-center" role={isLoading ? "status" : undefined} aria-live={isLoading ? "polite" : undefined}>
        <Icon
          className={`w-10 h-10 mx-auto mb-4 ${isLoading ? "animate-spin motion-reduce:hidden" : ""}`}
          style={{ color: iconColor }}
          aria-hidden="true"
        />
        {title && <h1 className="editorial-display">{title}</h1>}
        {body && (
          <div className="editorial-subtitle mb-6">
            {body}
          </div>
        )}
        {action && <div className="flex flex-col gap-2">{action}</div>}
      </div>
    </Crossfade>
  );
}
